/** Колокольчик в шапке: уведомления и новые сообщения обсуждений.
 *
 * Автор: «не видно, что пришло сообщение, пока в заявку не провалишься» и
 * «удобнее, если бы где-то было оповещение». Число — непрочитанные
 * уведомления + новые сообщения; по клику — список. Клик по строке
 * открывает заявку/закупку и гасит уведомление.
 */
import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../api/client";
import { useDiscussionUnread } from "./RequestDiscussion";
import { Counter, IconButton } from "../ui";

interface NotificationItem {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  created_at: string | null;
  read: boolean;
}

const NOTIFICATIONS_KEY = ["notifications"] as const;

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "";

export default function DiscussionBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const unread = useDiscussionUnread();
  const notes = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => api.get<{ unread: number; items: NotificationItem[] }>("/notifications?limit=20"),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const read = useMutation({
    mutationFn: (ids: string[] | null) => api.post("/notifications/read", { ids }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const discussions = unread.data ?? [];
  const items = notes.data?.items ?? [];
  const messages = discussions.reduce((sum, i) => sum + i.count, 0);
  const total = messages + (notes.data?.unread ?? 0);

  // Счётчик — в заголовке вкладки: человек видит «(2) РУСЬ ИИ» в списке
  // вкладок, не открывая систему.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = total > 0 ? `(${total}) ${base}` : base;
  }, [total]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openNote = (n: NotificationItem) => {
    if (!n.read) read.mutate([n.id]);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={box} className="relative">
      <IconButton
        icon="bell"
        label={total ? `Уведомления: ${total}` : "Новых уведомлений нет"}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="discussion-bell"
      />
      {total > 0 && (
        <span className="pointer-events-none absolute -right-1 -top-1">
          <Counter value={total} tone="danger" />
        </span>
      )}
      {open && (
        <div
          role="dialog"
          aria-label="Уведомления"
          className="absolute right-0 top-full z-drawer mt-1 w-[min(24rem,calc(100vw-2rem))] animate-scale-in rounded-lg border border-line bg-raised p-1 shadow-lg"
        >
          <div className="max-h-[28rem] overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-xs font-medium text-muted">Уведомления</span>
              {(notes.data?.unread ?? 0) > 0 && (
                <button
                  type="button"
                  className="text-xs text-accent hover:underline"
                  onClick={() => read.mutate(null)}
                >
                  Прочитать все
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <p className="px-2 pb-2 text-sm text-muted">Уведомлений пока нет.</p>
            ) : (
              <ul data-testid="notifications-list">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openNote(n)}
                      className={`block w-full rounded px-2 py-2 text-left text-sm hover:bg-sunken ${n.read ? "opacity-60" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />}
                        <span className="min-w-0 flex-1 font-medium text-ink">{n.title}</span>
                      </div>
                      {n.body && <div className="mt-0.5 line-clamp-2 text-xs text-muted">{n.body}</div>}
                      <div className="mt-0.5 text-2xs text-faint">{when(n.created_at)}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-1 border-t border-line px-2 py-1.5 text-xs font-medium text-muted">Обсуждения заявок</div>
            {discussions.length === 0 ? (
              <p className="px-2 pb-2 text-sm text-muted">Новых сообщений нет.</p>
            ) : (
              <ul>
                {discussions.map((i) => (
                  <li key={i.process_id}>
                    <Link
                      to={`/requests/${i.process_id}#discussion`}
                      onClick={() => setOpen(false)}
                      className="block rounded px-2 py-2 text-sm hover:bg-sunken"
                    >
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-xs text-faint">{i.number}</span>
                        <span className="min-w-0 flex-1 truncate font-medium text-ink">{i.title}</span>
                        {i.count > 1 && <Counter value={i.count} tone="danger" />}
                      </div>
                      <div className="mt-0.5 line-clamp-2 text-xs text-muted">
                        <span className="text-ink">{i.last_by_name ?? "—"}:</span> {i.last_body}
                      </div>
                      <div className="mt-0.5 flex gap-2 text-2xs text-faint">
                        <span>{when(i.last_at)}</span>
                        {i.awaiting_me && <span className="text-warning-ink">ждут вашего ответа</span>}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            <NotifyPrefs />
          </div>
        </div>
      )}
    </div>
  );
}

const PREF_OPTIONS: { value: "each" | "digest" | "none"; label: string }[] = [
  { value: "each", label: "на каждое событие" },
  { value: "digest", label: "сводкой раз в день" },
  { value: "none", label: "не присылать" },
];

/** Как слать письма о событиях. Колокольчик работает всегда; письмо на каждое
 *  сообщение обсуждения при живой переписке превращается в шум — отсюда
 *  «сводка раз в день» и «не присылать». */
function NotifyPrefs() {
  const { user, refresh } = useAuth();
  const id = useId();
  const save = useMutation({
    mutationFn: (notify_email: "each" | "digest" | "none") =>
      api.patch<unknown>("/auth/me/notifications", { notify_email }),
    onSuccess: () => void refresh(),
  });
  if (!user) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 border-t border-line px-2 py-2 text-xs text-muted">
      <label htmlFor={id}>Письма о событиях:</label>
      <select
        id={id}
        className="input h-7 py-0 text-xs"
        value={user.notify_email ?? "each"}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.value as "each" | "digest" | "none")}
      >
        {PREF_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {save.isError && <span className="text-danger-ink">не сохранилось</span>}
    </div>
  );
}
